// Access-denied gate page. Ported pixel-for-pixel from
// design/rev3/project/views-pending.jsx (AccessDenied).
// SECURITY: this page must NEVER name the allowed domain — it only echoes the
// account the user actually tried to sign in with.
import { useMsal } from '@azure/msal-react'
import { Icon } from '../components/icons'
import { logout } from '../auth/authActions'
import { hasOtpToken, otpLogout } from '../auth/otp/otpActions'
import { APP_NAME, APP_SUBTITLE } from '../config'
import { useSupportEmail } from '../lib/useSupportEmail'

export function AccessDenied({ email, onRetry, isOtp }: { email?: string; onRetry?: () => void; isOtp?: boolean }) {
  const handleRetry = onRetry ?? (() => logout())
  const supportEmail = useSupportEmail()
  return (
    <div className="vitals" style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', background: 'var(--cream)' }}>
      <header style={{ padding: '26px 40px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <div className="vt-brand-mark"><Icon.spark size={17} /></div>
        <div>
          <div className="vt-brand-name" style={{ color: 'var(--woodland)' }}>{APP_NAME}</div>
          <div className="vt-brand-sub">{APP_SUBTITLE}</div>
        </div>
      </header>

      <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 40px 80px' }}>
        <div style={{ maxWidth: 540, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--st-offline-soft)', border: '1px solid var(--st-offline-line)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 26, color: 'var(--st-offline)',
            boxShadow: 'var(--shadow-sm)' }}>
            <Icon.block size={30} />
          </div>
          <h1 style={{ fontSize: 31, fontWeight: 600, letterSpacing: '-0.6px', color: 'var(--woodland)', lineHeight: 1.2 }}>
            {isOtp ? 'Kein Zugriff' : 'Dieses Konto ist nicht zugelassen'}
          </h1>
          <p style={{ fontSize: 16, color: 'var(--ink-70)', marginTop: 16, lineHeight: 1.6 }}>
            {isOtp
              ? 'Deine Sitzung ist abgelaufen oder dein Zugang ist nicht freigegeben. Bitte melde dich erneut an oder wende dich an deine IT, wenn du Zugriff benötigst.'
              : 'Dein Microsoft-Konto ist für diese Anwendung nicht freigegeben. Bitte wende dich an deine IT, wenn du Zugriff benötigst.'}
          </p>

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 26, padding: '10px 16px',
            background: 'var(--surface)', border: '1px solid var(--st-offline-line)', borderRadius: 24, boxShadow: 'var(--shadow-sm)' }}>
            <span className="vt-status offline" style={{ fontSize: 13.5 }}><span className="vt-dot"></span>Zugriff verweigert</span>
          </div>

          {email && (
            <div style={{ marginTop: 38, paddingTop: 26, borderTop: '1px solid var(--line-strong)' }}>
              <div className="vt-eyebrow" style={{ fontSize: 11.5 }}>Anmeldeversuch mit</div>
              <div className="vt-mono" style={{ fontSize: 14, color: 'var(--ink)', marginTop: 4, fontWeight: 500 }}>{email}</div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 30 }}>
            <button className="vt-btn vt-btn-primary" style={{ padding: '11px 20px' }} onClick={handleRetry}>
              <Icon.refresh size={15} />{isOtp ? 'Erneut anmelden' : 'Mit anderem Konto anmelden'}
            </button>
          </div>

          <p style={{ fontSize: 13.5, color: 'var(--ink-50)', marginTop: 30 }}>
            Sollte das ein Fehler sein? <a href={`mailto:${supportEmail}`} style={{ color: 'var(--portugal)', fontWeight: 500, textDecoration: 'none' }}>IT kontaktieren</a>
          </p>
        </div>
      </main>
    </div>
  )
}

export default function AccessDeniedPage() {
  const { accounts } = useMsal()
  // OTP-Session (Token vorhanden, kein MSAL-Account): neutral abmelden statt
  // MSAL-logout(), das den OTP-Token nicht löscht und so die Sackgasse zementiert.
  const isOtp = accounts.length === 0 && hasOtpToken()
  if (isOtp)
    return (
      <AccessDenied
        isOtp
        onRetry={async () => {
          await otpLogout()
          window.location.assign('/')
        }}
      />
    )
  return <AccessDenied email={accounts[0]?.username} onRetry={() => logout()} />
}
